import { ITargetsParams } from '@cian/adfox/build/api';
import {
  calculatedParamsMiddleware,
  IAdFoxBannerTypeParams,
  IAdFoxParams,
  mergeMiddlewares,
} from '@cian/adfox/build/middlewares';
import { AdFoxBanner as AdFoxBannerElement, IAdFoxBannerContext } from '@cian/adfox/build/react';
import { LazyLoad } from '@cian/lazyload-component';

import { EAdFoxBannerType as EAdFoxBannerNewType, IAdFoxBannerOnLoadData } from '@cian/adfox-component';
import { AdFoxBanner as AdFoxBannerNew } from './new/NewAdfoxBanner';
import * as PropTypes from 'prop-types';
import * as React from 'react';

import { getAdfoxData } from '../../api/adfox';
import { getRandomId } from '../../utils/adfox';
import { PIKBanner, ParallaxBanner, EAdFoxBannerType, getBannerByType } from './templates';
import { connect } from 'react-redux';
import { IApplicationState } from '../../types/redux';

import * as styles from './Adfox.css';
import { isSPBOrMoscow } from '../../utils/geo/Capitals';
import { IRegion } from '../../types/regions/region';
import { IApplicationContext } from '../../types/applicationContext';
import { sendAdsEvent } from '../../utils/sendAdsEvent';

const PR_RANDOM = getRandomId();
const requestedBanners: string[] = [];

interface IAdFoxBannerOwnProps {
  type: EAdFoxBannerType;
  positionHint: string;
  onMount?(context: IAdFoxBannerContext): void;
  onFail?(): void;
}

interface IAdFoxBannerStoreProps {
  subdomain: string;
  currentRegion: IRegion;
  realtyUserId?: number | null;
}

export type TAdFoxBannerProps = IAdFoxBannerOwnProps & IAdFoxBannerStoreProps;

export interface IAdFoxBannerState {
  isActive: boolean;
}

class AdFoxBannerComponent extends React.PureComponent<TAdFoxBannerProps, IAdFoxBannerState> {
  public context: IApplicationContext;
  private bannerId: string;

  public static contextTypes = {
    httpApi: PropTypes.object,
    logger: PropTypes.object,
  };

  public state: IAdFoxBannerState = {
    isActive: false,
  };

  private randomId: string;
  private bannerMiddleware: () => Promise<IAdFoxParams>;

  public componentDidMount() {
    const { type, positionHint, currentRegion, subdomain } = this.props;

    this.randomId = getRandomId();
    this.bannerMiddleware = () =>
      bannerMiddleware(
        type,
        getAdfoxData({ httpApi: this.context.httpApi, parameters: { section_type: 6 }, subdomain }).then(response => {
          requestedBanners.push(positionHint);
          const customParams = {
            pr: PR_RANDOM,
            pr1: this.randomId,
            puid5:
              currentRegion && isSPBOrMoscow(currentRegion)
                ? `obl_id_${currentRegion.id}`
                : response.response.data.params.puid5,
          };

          return {
            ...response.response.data.params,
            ...customParams,
          };
        }),
      );

    this.setState({
      isActive: true,
    });
  }

  public render() {
    if (!this.state.isActive) {
      return null;
    }

    return (
      <LazyLoad
        offset={300}
        onLoad={this.bannerMiddleware}
        renderLoaded={this.renderLoaded}
        renderEmpty={() => <div />}
        renderError={this.renderError}
      />
    );
  }

  private renderLoaded = (adFoxParams: IAdFoxParams) => {
    const { pr, pr1, ...rest } = adFoxParams;

    return (
      <div className={styles['adfox']} onClick={this.onBannerClick}>
        {this.props.type === EAdFoxBannerType.PARALLAX ? (
          <AdFoxBannerNew
            type={EAdFoxBannerNewType.Async}
            ownerId={202100}
            // tslint:disable-next-line:no-any
            params={rest as any}
            onLoad={this.onNewBannerLoad}
          />
        ) : (
          <AdFoxBannerElement
            middleware={() => Promise.resolve(adFoxParams)}
            renderFetched={this.renderFetched}
            renderFailure={error => this.renderFailure(adFoxParams, error)}
          />
        )}
        {/* Пустой div для места РСЯ баннера (РСЯ баннер может быть вместо любого баннера) */}
        <div id={`AdFox_banner_${this.randomId}`} />
      </div>
    );
  }

  private onNewBannerLoad = (data: IAdFoxBannerOnLoadData) => {
    this.bannerId = data.bundleParams.bannerId;

    sendAdsEvent({
      realtyUserId: this.props.realtyUserId,
      action: 'show',
      bannerId: data.bundleParams.bannerId,
      bannerPlace: 'newbuildingsSearch',
    });
  }

  private onBannerClick = () => {
    sendAdsEvent({
      realtyUserId: this.props.realtyUserId,
      action: 'click',
      bannerId: this.bannerId,
      bannerPlace: 'newbuildingsSearch',
    });
  }

  private renderFetched = (context: IAdFoxBannerContext) => {
    switch (context['type'] as EAdFoxBannerType) {
      case EAdFoxBannerType.PIK:
        return <PIKBanner context={context} onMount={() => this.onMount(context)} />;

      case EAdFoxBannerType.PARALLAX:
        return <ParallaxBanner context={context} onMount={() => this.onMount(context)} />;

      default:
        return <div />;
    }
  }

  private onMount = (context: IAdFoxBannerContext) => {
    const { onMount } = this.props;

    if (onMount) {
      onMount(context);
    }

    this.bannerId = context.analyticsBannerId;

    sendAdsEvent({
      realtyUserId: this.props.realtyUserId,
      action: 'show',
      bannerId: context.analyticsBannerId,
      bannerPlace: 'newbuildingsSearch',
    });
  }

  private renderFailure = (adFoxParams: IAdFoxParams, error: Error) => {
    const { type, positionHint, onFail } = this.props;

    if (onFail) {
      onFail();
    }

    //TODO: logger id undefined
    this.context.logger.warning('AdFox rendering error', {
      positionInfo: positionHint,
      puids: adFoxParams,
      urls: window.location.href,
      bannerType: type,
      isAdblock: !window._cianCanRunAds,
    });

    return <div />;
  }

  /**
   * Если баннер запросился, запрос выполнился без ошибки, но по какой-то причине баннер не отобразился
   * (например из-за того что пришел не валидный код для отображения баннера - с помощью document.write)
   * логируем НЕ показ баннера
   */
  private renderError = () => {
    const { type, positionHint, onFail } = this.props;

    if (onFail) {
      onFail();
    }

    this.context.logger.warning('AdFox rendering error', {
      positionInfo: positionHint,
      puids: '',
      urls: window.location.href,
      bannerType: type,
      isAdblock: !window._cianCanRunAds,
    });

    return <div />;
  }
}

function bannerMiddleware(
  bannerType: EAdFoxBannerType,
  request: Promise<IAdFoxBannerTypeParams>,
  targetParams: ITargetsParams = {},
): Promise<IAdFoxParams> {
  return mergeMiddlewares(request, typeParamsMiddleware(bannerType), calculatedParamsMiddleware());
}

function typeParamsMiddleware(type: EAdFoxBannerType): Promise<IAdFoxBannerTypeParams> {
  return Promise.resolve(getBannerByType(type));
}

function mapStateToProps(state: IApplicationState): IAdFoxBannerStoreProps {
  return {
    currentRegion: state.region.currentRegion,
    subdomain: state.location.subdomain || 'www',
    realtyUserId: state.user.isAuthenticated ? state.user.userId : undefined,
  };
}

export const AdFoxBanner = connect(mapStateToProps)(AdFoxBannerComponent);
